import { useEffect, useRef } from 'react';

// ─── Compose-draft persistence ───────────────────────────────────
// Debounced autosave of a single text value to localStorage, restore-on-mount,
// and an explicit clear() for the success path. This is the safety net beyond
// "the modal stays open": it survives an accidental modal close, a browser
// refresh, or a crash mid-compose. Defensive try/catch (mirrors lib/prefs.ts)
// so private mode / quota errors degrade silently.

const PREFIX = 'pcp:draft:';
const SAVE_DEBOUNCE_MS = 600;

/** Stable per-surface, per-entity, per-user key. Pass a userId so two accounts
 *  on one browser never see each other's drafts. Empty/nullish parts are dropped. */
export function draftKey(parts: Array<string | number | undefined | null>): string {
  return PREFIX + parts.filter((p) => p !== undefined && p !== null && p !== '').join(':');
}

/** Read a raw draft string (null if absent / storage unavailable). Exported for
 *  callers whose restore must be driven by their own seed logic rather than the
 *  hook's mount-once restore (e.g. an always-mounted modal that re-seeds on open). */
export function readDraft(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
/** Write a raw draft string (no-op on quota / private-mode errors). */
export function writeDraft(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* quota / private mode — ignore */ }
}
export function clearDraft(key: string): void {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/**
 * Autosave `value` to localStorage under `key` (debounced), restore it once on
 * mount, and return a `clear()` to call after a SUCCESSFUL submit.
 *
 * - Restores once on mount, but ONLY when the current value is empty, so it never
 *   clobbers server-seeded content on an edit form.
 * - Autosaves whenever `value` changes and is non-empty; clears the key when the
 *   value becomes empty (so an emptied composer doesn't leave a stale draft).
 *
 * `isEmpty` lets callers treat e.g. an empty rich-text "<p></p>" as empty.
 *
 * `flushOnUnmount` writes the latest value through when the surface goes away, so
 * what was typed inside the last debounce window survives a dismissal that comes
 * right after it. Opt-in: a caller that clears the stored draft after a submit but
 * closes WITHOUT emptying its value would otherwise write the sent text straight
 * back. A caller using it should empty its value (and `clear()`) when the submit
 * STARTS, not once it resolves: a surface dismissed and reopened while the submit is
 * in flight would otherwise flush the in-flight text and restore it into the new
 * one, ready to be sent again. Put it back only if the submit fails. (b-02030)
 */
export function useDraft(
  key: string,
  value: string,
  setValue: (v: string) => void,
  opts?: { enabled?: boolean; isEmpty?: (v: string) => boolean; flushOnUnmount?: boolean },
): { clear: () => void } {
  const enabled = opts?.enabled ?? true;
  const isEmpty = opts?.isEmpty ?? ((v: string) => !v || !v.trim());
  const flushOnUnmount = opts?.flushOnUnmount ?? false;
  const restoredRef = useRef(false);
  const latestRef = useRef(value);
  latestRef.current = value;

  // Restore once.
  useEffect(() => {
    if (!enabled || restoredRef.current) return;
    restoredRef.current = true;
    const saved = readDraft(key);
    if (saved && isEmpty(value)) {
      setValue(saved);
      // An unmount before that render lands must still write it back, not clear it.
      latestRef.current = saved;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  // Debounced autosave.
  useEffect(() => {
    if (!enabled) return;
    if (isEmpty(value)) { clearDraft(key); return; }
    const t = setTimeout(() => writeDraft(key, value), SAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, value, enabled]);

  // Write-through on unmount (or on a key change, for the key being left).
  useEffect(() => {
    if (!enabled || !flushOnUnmount) return;
    return () => {
      const v = latestRef.current;
      if (isEmpty(v)) clearDraft(key); else writeDraft(key, v);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, flushOnUnmount]);

  return { clear: () => clearDraft(key) };
}
